import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CaretDown, CircleNotch, Copy, LinkSimple, Printer, ShoppingCartSimple } from "@phosphor-icons/react";

export function DashboardActionMenu({
  isCustomer,
  selectedCount,
  onAddSelectedToCart,
  onPrintGridView,
  onGenerateLink,
  customers = [],
  exportCustomerId = "none",
  onExportCustomerChange,
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [linkCustomerId, setLinkCustomerId] = useState("none");
  const [generating, setGenerating] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    const customer = customers.find((c) => c.id === linkCustomerId);
    const coefficient = customer?.coefficient || 1;
    setGenerating(true);
    const result = await onGenerateLink(coefficient);
    setGenerating(false);
    if (result) {
      setLink(result);
      setCopied(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      /* manual copy */
    }
  };

  const openLinkDialog = () => {
    setLink(null);
    setLinkCustomerId(exportCustomerId || "none");
    setLinkOpen(true);
  };

  const customerSelect = (value, onChange, testId) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-44 rounded-sm border-zinc-300 text-sm" data-testid={testId}>
        <SelectValue placeholder="No customer" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No customer (x1)</SelectItem>
        {customers.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name} ({c.currency}, x{c.coefficient})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <>
      {!isCustomer && (
        <div className="flex items-center gap-1.5" title="Customer whose price coefficient is applied to exported documents">
          <span className="whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.05em] text-zinc-400">Customer</span>
          {customerSelect(exportCustomerId, onExportCustomerChange, "products-export-customer-select")}
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" className="rounded-sm bg-blue-600 text-white hover:bg-blue-700" data-testid="products-action-menu-button">
            Action
            <CaretDown size={14} className="ml-1.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="rounded-sm">
          {!isCustomer && (
            <>
              <DropdownMenuItem
                disabled={selectedCount === 0 || addingToCart}
                onClick={async () => {
                  if (!onAddSelectedToCart) return;
                  setAddingToCart(true);
                  try {
                    await onAddSelectedToCart();
                  } finally {
                    setAddingToCart(false);
                  }
                }}
                data-testid="action-add-to-quote-button"
              >
                {addingToCart ? (
                  <CircleNotch size={14} className="mr-2 animate-spin" />
                ) : (
                  <ShoppingCartSimple size={14} className="mr-2" />
                )}
                Add to Quote{selectedCount > 0 ? ` (${selectedCount})` : ""}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={selectedCount === 0}
                onClick={onPrintGridView}
                data-testid="action-print-grid-view-button"
              >
                <Printer size={14} className="mr-2" />
                Print Grid View{selectedCount > 0 ? ` (${selectedCount})` : ""}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={selectedCount === 0}
                onClick={openLinkDialog}
                data-testid="action-generate-link-button"
              >
                <LinkSimple size={14} className="mr-2" />
                Generate Link{selectedCount > 0 ? ` (${selectedCount})` : ""}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="generate-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Link</DialogTitle>
            <DialogDescription>
              Creates a public link (valid 30 days, no login needed) with the {selectedCount} product
              {selectedCount === 1 ? "" : "s"} you've selected. Prices are multiplied by the coefficient below.
            </DialogDescription>
          </DialogHeader>
          {link ? (
            <>
              <div className="break-all rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700" data-testid="generated-link-text">
                {link}
              </div>
              <DialogFooter>
                <Button className="rounded-sm bg-blue-600 text-white hover:bg-blue-700" onClick={handleCopy} data-testid="copy-generated-link-button">
                  <Copy size={14} className="mr-1.5" /> {copied ? "Copied!" : "Copy Link"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-[0.05em] text-zinc-500">Customer (Price Coefficient)</Label>
                {customerSelect(linkCustomerId, setLinkCustomerId, "link-customer-select")}
              </div>
              <DialogFooter>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                  data-testid="generate-link-submit-button"
                >
                  {generating ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <LinkSimple size={14} className="mr-1.5" />}
                  Generate
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
